import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DateTime, DateTimeUnit, DurationLike } from "luxon";
import { combineLatest, concatMap, map, Observable, of } from "rxjs";
import { ASSET_TYPES } from "../../../enums/assetTypes.enum";
import { AssetService } from "../../api/asset/asset.service";
import { TelemetryService } from "../../api/telemetry/telemetry.service";
import { cloneDeep } from "lodash";
import { DataPoint, EnergyLossData } from "../../../models/ui/energyToolCharts";

import { GateSystemCompact } from "../../../models/api/asset/assetCompact";
import { TelemetryEntry } from "../../../models/shared/telemetry";
import { getAssetChildrenType } from "../../../shared/utils/assetType.utils";

@Injectable({
  providedIn: "root",
})
export class EnergyToolUtilsService {
  private translate = inject(TranslateService);
  private assetService = inject(AssetService);
  private telemetryService = inject(TelemetryService);

  /**
   * Get the number of cycles from the given timeseries of cycles.
   * If the timeseries is empty, returns 0.
   * Otherwise, returns the difference between the first and the last data point.
   * @param cycles - The timeseries of cycles
   * @returns The number of cycles
   */
  public getNumberOfCyclesFromCycleTimeseries(cycles: TelemetryEntry[]) {
    let numberOfCycles: number;
    if (cycles.length === 0) {
      numberOfCycles = 0;
    } else {
      const firstDataPoint = cycles[0].value;
      const lastDataPoint = cycles[cycles.length - 1].value;
      numberOfCycles = firstDataPoint - lastDataPoint;
    }

    return numberOfCycles;
  }

  /**
   * Generates a chart data array with points representing the expected energy loss for each month of the year.
   * The target value is divided among the months of the year in a fraction of the total days in the year.
   * @param target total expected energy loss
   * @returns array of DataPoints representing expected energy loss for each month of the year
   */
  public createExpectedChart(target: number): DataPoint[] {
    let currentDay = DateTime.now().startOf("year");
    const totalDays = currentDay.daysInYear;
    const points: DataPoint[] = [];

    // 12 Elements corresponding to 12 Months of the year:
    const monthlyFraction = [0.2, 0.12, 0.1, 0.09, 0.03, 0.02, 0.01, 0, 0.04, 0.08, 0.14, 0.17];
    let cumulativeValue = 0;
    for (let i = 0; i < totalDays; i++) {
      const fraction = monthlyFraction[currentDay.month - 1];
      const incrementValue = (target * fraction) / currentDay.daysInMonth;
      cumulativeValue += incrementValue;
      points.push({
        x: currentDay.toMillis(),
        y: cumulativeValue,
      });
      currentDay = currentDay.plus({ days: 1 });
    }
    return points;
  }
  /**
   * Convert a timestamp to a friendly display string.
   *
   * @param timestamp timestamp to convert
   * @returns timestamp converted to a friendly display string
   */
  public convertTimeStamp(timestamp: number | undefined) {
    if (timestamp !== undefined) {
      const now = DateTime.now();
      const lastEvent = DateTime.fromMillis(timestamp);
      let timeDisplay;
      if (timestamp === 0) {
        timeDisplay = "";
      } else if (lastEvent.hasSame(now, "day")) {
        timeDisplay = this.translate.instant("common.today_capital") + " " + lastEvent.toFormat("HH:mm");
      } else if (lastEvent.hasSame(now.minus({ days: 1 }), "day")) {
        timeDisplay = this.translate.instant("common.yesterday_capital") + " " + lastEvent.toFormat("HH:mm");
      } else if (lastEvent > now.minus({ days: 6 })) {
        timeDisplay = lastEvent.setLocale(this.translate.currentLang).toFormat("cccc HH:mm");
      } else {
        timeDisplay = lastEvent.toFormat("dd.LL.yyyy HH:mm");
      }
      return timeDisplay;
    } else {
      return "";
    }
  }
  /**
   * @param energyLoss Energy loss data
   * @param timeWindow Time window string (today, yesterday, this_week, this_month, this_year)
   * @returns Sum of energy loss in the given time window, or undefined if no data is available.
   */
  public getTimeWindowEnergyLoss(energyLoss: TelemetryEntry[], timeWindow: string) {
    let startingPoint;
    let endingPoint;
    switch (timeWindow) {
      case "today":
        startingPoint = DateTime.now().startOf("day").toMillis();
        endingPoint = DateTime.now().endOf("day").toMillis();
        break;
      case "yesterday":
        startingPoint = DateTime.now().startOf("day").minus({ day: 1 }).toMillis();
        endingPoint = DateTime.now().endOf("day").minus({ day: 1 }).toMillis();
        break;
      case "this_week":
        startingPoint = DateTime.now().startOf("week").toMillis();
        endingPoint = DateTime.now().endOf("week").toMillis();
        break;
      case "this_month":
        startingPoint = DateTime.now().startOf("month").toMillis();
        endingPoint = DateTime.now().endOf("month").toMillis();
        break;
      case "this_year":
        startingPoint = DateTime.now().startOf("year").toMillis();
        endingPoint = DateTime.now().endOf("year").toMillis();
        break;
      default:
        startingPoint = DateTime.now().startOf("day").toMillis();
        endingPoint = DateTime.now().endOf("day").toMillis();
        break;
    }

    if (startingPoint && endingPoint && energyLoss) {
      const consideredPoints = cloneDeep(
        energyLoss.filter((point) => {
          return point.timestamp >= startingPoint && point.timestamp <= endingPoint;
        })
      );

      if (consideredPoints.length === 0) {
        return 0;
      }
      const sumOfConsideredPoints = consideredPoints.reduce((prev, curr) => {
        // For testing add this: + Math.round(Math.random() * 3);
        prev.value = prev.value + curr.value;
        return prev;
      });
      return sumOfConsideredPoints.value;
    } else {
      return undefined;
    }
  }

  /**
   * Given a time window (one of today, yesterday, this_week, this_month, this_year)
   * returns the start and end timestamps of that time window.
   * @param timeWindow
   * @returns {start: number, end: number}
   */
  public getStartAndEnd(timeWindow: string) {
    let start: number;
    let end: number;
    switch (timeWindow) {
      case "today":
        start = DateTime.now().startOf("day").toMillis();
        end = DateTime.now().endOf("day").toMillis();
        break;
      case "yesterday":
        start = DateTime.now().minus({ days: 1 }).startOf("day").toMillis();
        end = DateTime.now().minus({ days: 1 }).endOf("day").toMillis();
        break;
      case "this_week":
        start = DateTime.now().startOf("week").toMillis();
        end = DateTime.now().endOf("week").toMillis();
        break;
      case "this_month": {
        const startDate = DateTime.now().startOf("month");
        const endDate = DateTime.now().endOf("month");

        start = startDate.toMillis();
        end = endDate.toMillis();
        break;
      }
      default:
        start = DateTime.now().startOf("day").toMillis();
        end = DateTime.now().endOf("day").toMillis();
        break;
    }

    return { start, end };
  }

  /**
   * Gets the grouping level (e.g. hour, day, month) based on the view level (e.g. day, month, year).
   *
   * @param viewLevel The view level to base the grouping level on.
   * @returns The grouping level.
   */
  public getGroupingLevel(viewLevel: DateTimeUnit): DateTimeUnit {
    switch (viewLevel) {
      case "day":
        return "hour";
      case "month":
        return "day";
      case "week":
        return "day";
      case "year":
        return "month";
      default:
        return "day";
    }
  }

  /**
   * Converts a timeseries into a chart data format.
   *
   * @param data Telemetry data to convert.
   * @param groupingLevel Level to group the data by (e.g. hour, day, month).
   * @param viewLevel Level to round the timestamps to (e.g. day, month, year).
   * @param fillEmpty If true, fill in missing data points with null values.
   * @param time Optional timestamp to start filling in the data from.
   * @param accumulatedPoints If true, accumulate values of points over time.
   * @returns Array of chart data points.
   */
  public convertTimeseriesToChartData(
    data: TelemetryEntry[] | undefined,
    groupingLevel: DateTimeUnit,
    viewLevel: DateTimeUnit,
    fillEmpty: boolean,
    time?: number,
    accumulatedPoints = false
  ) {
    if (!data) {
      return undefined;
    }
    const groupedData = data.reduce((acc: any, point: any) => {
      // Round down to the start of the grouping level
      const group = DateTime.fromMillis(point.timestamp).startOf(groupingLevel).toISO();
      if (group) {
        if (!acc[group]) {
          acc[group] = 0;
        }
        if (point.value !== undefined && point.value !== null && point.value !== "NaN") {
          // for testing add this: + Math.round(Math.random() * 10);
          acc[group] += point.value;
        }
      }

      return acc;
    }, {});
    const aggregatedData = Object.keys(groupedData).map((group) => ({
      x: DateTime.fromISO(group).toMillis(),
      y: groupedData[group],
    }));

    if (fillEmpty && time !== undefined) {
      const startingPoint = DateTime.fromMillis(time).startOf(viewLevel);
      const endPoint = DateTime.fromMillis(time).endOf(viewLevel);

      let currentTime = startingPoint;
      while (currentTime <= endPoint) {
        const hour: string | null = currentTime.toISO();
        if (hour && !groupedData[hour]) {
          aggregatedData.push({
            x: currentTime.toMillis(),
            y: null,
          });
        }
        currentTime = currentTime.plus(this.getGroupingLevelCalculation(viewLevel));
      }
    }

    if (accumulatedPoints) {
      aggregatedData
        .sort((a, b) => a.x - b.x)
        .forEach((point, index) => {
          const previousPointIndex = index - 1;
          if (previousPointIndex >= 0) {
            const previousPoint = aggregatedData[previousPointIndex];
            point.y += previousPoint.y;
          }
        });
    }

    return aggregatedData;
  }

  /**
   * Calculates the average value of the given data points,
   * only considering those with a timestamp before the current time.
   *
   * @param data - the data points to calculate the average for
   * @returns the average value or an array of data points with y=0 if the sum is 0
   */
  public calcAverage(data: any[]) {
    let sum = 0;
    let consideredPoints = 0;
    const now = DateTime.now().toMillis();
    for (const point of data) {
      if (point.x < now) {
        sum += point.y;
        consideredPoints++;
      }
    }
    if (sum === 0) {
      return data.map((point) => {
        return { x: point.x, y: 0 };
      });
    } else {
      const average = sum / consideredPoints;
      return data.map((point) => {
        return { x: point.x, y: average };
      });
    }
  }

  /**
   * Calculates the duration between each point of the given view level.
   *
   * @param viewLevel - the view level to calculate the duration for
   * @returns the duration between each point of the given view level
   */
  public getGroupingLevelCalculation(viewLevel: DateTimeUnit): DurationLike {
    switch (viewLevel) {
      case "day":
        return { hours: 1 };
      case "month":
        return { days: 1 };
      case "week":
        return { days: 1 };
      case "year":
        return { months: 1 };
      default:
        return { days: 1 };
    }
  }

  /**
   * Returns true if the given data has only one point with a y-value, or false otherwise.
   * @param data The data to check.
   * @returns True if the given data has only one point with a y-value, or false otherwise.
   */
  public checkIfSinglePoint(data: DataPoint[] | undefined) {
    if (data) {
      data = data.filter((point) => point.y !== null && point.y !== undefined);
      return data.length === 1;
    } else {
      return false;
    }
  }

  /**
   * Returns an observable of energy loss data for all children of the given asset, or null if there are no children.
   * @param assetId The ID of the asset.
   * @param assetType The type of the asset.
   * @param startTime The start time of the time range.
   * @param endTime The end time of the time range.
   * @returns An observable of energy loss data for all children of the given asset, or null if there are no children.
   */
  public getChildrenEnergyLossObservable(
    assetId: string,
    assetType: string,
    startTime: number,
    endTime: number
  ): Observable<EnergyLossData[] | null> {
    const childrenType = getAssetChildrenType(assetType);
    const assetChildren$ = this.assetService.getAssets(childrenType, assetId, assetType);
    const childrenEnergyLoss$ = assetChildren$.pipe(
      concatMap((children) => {
        const childrenEnergy$: Observable<EnergyLossData>[] = [];
        if (children && childrenType) {
          children.forEach((child) => {
            if (child) {
              if (childrenType === ASSET_TYPES.GATE_SYSTEM) {
                const gateSystem = child as GateSystemCompact;
                if (!gateSystem.core || !gateSystem.hasGuard) {
                  return;
                }
              }
              if (child.id) {
                childrenEnergy$.push(
                  this.telemetryService.getTimeseries(child.id, childrenType, "energy_loss", startTime, endTime).pipe(
                    map((res) => {
                      const energyLossData: EnergyLossData = {
                        source: { id: child.id, label: child.label },
                        energy_loss: res.energy_loss,
                      };
                      return energyLossData;
                    })
                  )
                );
              }
            }
          });
          if (childrenEnergy$.length > 0) {
            return combineLatest(childrenEnergy$);
          } else {
            return of([]);
          }
        } else {
          return of([]);
        }
      })
    );

    return childrenEnergyLoss$;
  }
}
